import { Component } from 'react';
import PropTypes from 'prop-types';
// jl-design-system
import Form from 'jl-design-system/components/form/Form';
// redux
import { connect } from 'react-redux';
import { setFieldValue, unTouchForm } from 'jl-design-system/redux/actions/form/formActions';
import { getCardType, clearCardType } from '../../../../redux/actions/payment/cardTypeAction';
// config
import {
  getCardDetailsFormConfig,
  CARD_DETAILS_FORM_ID,
  CARD_NUMBER,
  PREPAID_CARD_DETAILS_FORM_ID,
} from '../../../../utils/form/configs/cardDetails';
import Container from '@components/container';
import { isFeatureActive } from '@redux/reducers/config/configReducer';
import featureConstants from '@constants/featureConstants';
import { recordStripeLayoutImpressionId } from '@redux/actions/payment/paymentActions';
// utils
import stripWhitespace from '../../../../utils/string/stripWhitespace';
import styles from './credit-card-form.scss';

export class CreditCardForm extends Component {

  static propTypes = {
    cardMaxLength: PropTypes.number.isRequired,
    cardMinLength: PropTypes.number.isRequired,
    cardNumber: PropTypes.string,
    cardType: PropTypes.string,
    clearCardType: PropTypes.func,
    disabled: PropTypes.bool,
    getCardType: PropTypes.func,
    prepaidCard: PropTypes.bool,
    recordStripeLayoutImpressionId: PropTypes.func,
    resetAfterError: PropTypes.number,
    securityCodeLength: PropTypes.number.isRequired,
    setFieldValue: PropTypes.func,
    stripeLayoutFeatureEnabled: PropTypes.bool,
    unTouchForm: PropTypes.func,
  };

  static defaultProps = {
    cardNumber: '',
    cardType: '',
    clearCardType: window.defaultFunc,
    disabled: false,
    getCardType: window.defaultFunc,
    prepaidCard: false,
    recordStripeLayoutImpressionId: window.defaultFunc,
    resetAfterError: undefined,
    setFieldValue: window.defaultFunc,
    stripeLayoutFeatureEnabled: false,
    unTouchForm: window.defaultFunc,
  };

  static getDerivedStateFromProps(props, state) {
    const { oldPropsCardType } = state;

    if (oldPropsCardType !== props.cardType) {
      const {
        cardType,
        cardMaxLength,
        cardMinLength,
        prepaidCard,
        securityCodeLength,
        stripeLayoutFeatureEnabled,
      } = props;

      const formConfig = getCardDetailsFormConfig({
        cardType,
        cardMaxLength,
        cardMinLength,
        prepaidCard,
        securityCodeLength,
        stripeLayoutFeatureEnabled,
      });

      return {
        config: formConfig,
        oldPropsCardType: props.cardType,
      };
    }

    return {
      oldPropsCardType: props.cardType,
    };
  }

  constructor(props) {
    super(props);

    const {
      cardType,
      cardMaxLength,
      cardMinLength,
      prepaidCard,
      securityCodeLength,
      stripeLayoutFeatureEnabled,
    } = this.props;

    this.state = {
      config: getCardDetailsFormConfig({
        cardType,
        cardMaxLength,
        cardMinLength,
        prepaidCard,
        securityCodeLength,
        stripeLayoutFeatureEnabled,
      }),
      oldPropsCardType: cardType,
    };
  }

  componentDidMount() {
    const {
      setFieldValue,
      cardNumber,
      getCardType,
      cardType,
      clearCardType,
      recordStripeLayoutImpressionId,
      prepaidCard,
    } = this.props;

    if (cardNumber?.length >= 8) getCardType(cardNumber);

    // make sure that card type is unset if it exists without card number
    if (!cardNumber && cardType) clearCardType();

    setFieldValue(this.state.config.id, 'securityCode', '');
    if (!prepaidCard) recordStripeLayoutImpressionId();
  }

  componentDidUpdate(prevProps) {

    const {
      cardNumber,
      cardType,
      resetAfterError,
      unTouchForm,
      setFieldValue,
      clearCardType,
      getCardType,
    } = this.props;

    const {
      config,
    } = this.state;

    const SECURITY_CODE_FIELD = 'securityCode';

    const prevCardNumber = stripWhitespace(prevProps.cardNumber).substr(0, 8);
    const currCardNumber = stripWhitespace(cardNumber).substr(0, 8);

    if (currCardNumber.length === 8 && prevCardNumber !== currCardNumber) {
      getCardType(currCardNumber);
      return;
    }

    if (prevProps.cardType && prevProps.cardType !== cardType) {
      setFieldValue(config.id, SECURITY_CODE_FIELD, '');
    }

    if (resetAfterError !== undefined) {
      setFieldValue(config.id, SECURITY_CODE_FIELD, '');
      unTouchForm(config.id, [SECURITY_CODE_FIELD]);
    }

    if (cardType && currCardNumber.length < 8) {
      clearCardType();
    }
  }

  render() {
    const {
      config,
    } = this.state;

    const {
      disabled,
      stripeLayoutFeatureEnabled,
    } = this.props;

    return (
      <Container className={stripeLayoutFeatureEnabled ? styles.stripeLayout : null}>
        <Form
          config={config}
          data-testid="credit-card-form"
          destroyOnUnmount={false}
          disabled={disabled}
          form={config.id}
          multiFormValidation
          scrollValidationMessageIntoView
          useV2Message
        />
      </Container>
    );
  }
}

export function mapStateToProps(state, ownProps) {
  const cardNumber = ownProps.prepaidCard
    ? state.form?.[PREPAID_CARD_DETAILS_FORM_ID]?.values?.[CARD_NUMBER.name] ?? ''
    : state.form?.[CARD_DETAILS_FORM_ID]?.values?.[CARD_NUMBER.name] ?? '';

  const stripeLayoutFeatureEnabled = isFeatureActive(state, featureConstants.STRIPE_LAYOUT);

  return {
    cardNumber,
    cardType: state.cardType?.name,
    cardMaxLength: state.cardType?.cardNumberMaxLength,
    cardMinLength: state.cardType?.cardNumberMinLength,
    securityCodeLength: state.cardType?.securityCodeLength,
    stripeLayoutFeatureEnabled,
  };
}

export function mapDispatchToProps() {
  return {
    getCardType,
    clearCardType,
    setFieldValue,
    unTouchForm,
    recordStripeLayoutImpressionId,
  };
}

export default connect((state, ownProps) => mapStateToProps(state, ownProps), mapDispatchToProps())(CreditCardForm);
